import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export type Investment = Database["public"]["Tables"]["investments"]["Row"];

const sanitizeInvestmentPayload = (investment: Partial<Investment>) => ({
  title: investment.title ?? "",
  location: investment.location ?? "",
  type: investment.type ?? "",
  price: investment.price ?? "",
  roi: investment.roi ?? "",
  completion: investment.completion ?? "",
  description: investment.description ?? "",
  features: investment.features ?? [],
  image: investment.image ?? "",
  is_featured: investment.is_featured ?? false,
});

export const useInvestments = (featured?: boolean) => {
  return useQuery({
    queryKey: ["investments", featured],
    queryFn: async () => {
      let query = supabase.from("investments").select("*").order("created_at", { ascending: false });
      if (featured !== undefined) query = query.eq("is_featured", featured);
      const { data, error } = await query;
      if (error) throw error;
      return data as Investment[];
    },
  });
};

export const useInvestmentMutations = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["investments"] });

  const create = useMutation({
    mutationFn: async (investment: Partial<Investment>) => {
      const { data, error } = await supabase.from("investments").insert(sanitizeInvestmentPayload(investment)).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Investment> & { id: string }) => {
      const { data, error } = await supabase
        .from("investments")
        .update({ ...sanitizeInvestmentPayload(updates), updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("investments").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
};
